/**
 * DraggableComponent
 *
 * Draggable/resizable wrapper for label components.
 * Uses react-rnd for drag and resize with constraint enforcement.
 *
 * Features:
 * - React.memo for performance (only re-renders when this component changes)
 * - Design tokens for theming
 * - Touch-friendly resize handles (24px)
 * - Constraint feedback (min size indicator)
 * - Locked state with visual badge
 *
 * Key architecture decisions:
 * - Only updates store on onDragStop/onResizeStop (not during drag)
 * - This prevents undo from capturing every pixel of movement (Pitfall 4)
 * - Uses controlled position/size props (Pattern 1)
 * - NO motion.div wrapper (was causing bounds="parent" to use wrong element)
 */

import React, { useState, useCallback, useMemo, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd, type RndDragCallback, type RndResizeCallback } from 'react-rnd';
import { Lock } from 'lucide-react';
import { useLabelLayoutStore } from './label-layout-store';
import {
  PX_PER_MM,
  COMPONENT_CONSTRAINTS,
  COMPONENT_LABEL_KEYS,
  type ComponentConfig,
} from './label-layout-types';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface DraggableComponentProps {
  config: ComponentConfig;
  children: React.ReactNode;
}

// ============================================================================
// COMPONENT
// ============================================================================

function DraggableComponentInner({ config, children }: DraggableComponentProps) {
  const { t } = useTranslation('settings');
  const scale = useLabelLayoutStore((s) => s.editor.scale);
  const snapToGrid = useLabelLayoutStore((s) => s.editor.snapToGrid);
  const gridSizeMm = useLabelLayoutStore((s) => s.editor.gridSizeMm);
  const selectedId = useLabelLayoutStore((s) => s.editor.selectedComponentId);
  const updateComponent = useLabelLayoutStore((s) => s.updateComponent);
  const setSelectedComponent = useLabelLayoutStore((s) => s.setSelectedComponent);

  const constraints = COMPONENT_CONSTRAINTS[config.type];
  const isSelected = selectedId === config.id;

  // Track if we're at minimum size (for visual feedback)
  const [isAtMinSize, setIsAtMinSize] = useState(false);

  // Convert mm to pixels at current scale
  const toPixels = useCallback((mm: number) => mm * PX_PER_MM * scale, [scale]);
  const toMm = useCallback((px: number) => px / PX_PER_MM / scale, [scale]);

  /**
   * Snap a value in mm to the nearest grid point.
   *
   * NOTE: We do NOT use react-rnd's dragGrid/resizeGrid props because they snap
   * relative to the component's starting position, not to absolute canvas coordinates.
   * Manual snapping in onDragStop/onResizeStop ensures components align to the visual grid.
   */
  const snapToGridMm = useCallback(
    (mm: number): number => {
      if (!snapToGrid) return mm;
      return Math.round(mm / gridSizeMm) * gridSizeMm;
    },
    [snapToGrid, gridSizeMm]
  );

  // Memoize pixel values to prevent unnecessary recalculations
  const position = useMemo(
    () => ({ x: toPixels(config.position.x), y: toPixels(config.position.y) }),
    [config.position.x, config.position.y, toPixels]
  );

  const size = useMemo(
    () => ({ width: toPixels(config.size.width), height: toPixels(config.size.height) }),
    [config.size.width, config.size.height, toPixels]
  );

  // Min/max sizes in pixels
  const minWidth = constraints.minWidth ? toPixels(constraints.minWidth) : 20;
  const minHeight = constraints.minHeight ? toPixels(constraints.minHeight) : 20;
  const maxWidth = constraints.maxWidth ? toPixels(constraints.maxWidth) : undefined;
  const maxHeight = constraints.maxHeight ? toPixels(constraints.maxHeight) : undefined;

  // Handle drag end - only update store here (not during drag)
  // Manual snapping ensures alignment to absolute canvas grid coordinates
  const handleDragStop: RndDragCallback = useCallback(
    (_e, d) => {
      const xMm = snapToGridMm(toMm(d.x));
      const yMm = snapToGridMm(toMm(d.y));
      updateComponent(config.id, {
        position: { x: xMm, y: yMm },
      });
    },
    [config.id, toMm, snapToGridMm, updateComponent]
  );

  // Handle resize - check for minimum size constraint
  const handleResize: RndResizeCallback = useCallback(
    (_e, _dir, ref, _delta, _position) => {
      const newWidth = parseInt(ref.style.width);
      const newHeight = parseInt(ref.style.height);

      // Check if at constraint boundary
      const atMinWidth = constraints.minWidth && toMm(newWidth) <= constraints.minWidth + 0.5;
      const atMinHeight = constraints.minHeight && toMm(newHeight) <= constraints.minHeight + 0.5;
      setIsAtMinSize(Boolean(atMinWidth || atMinHeight));
    },
    [constraints, toMm]
  );

  // Handle resize end - only update store here
  // Manual snapping ensures alignment to absolute canvas grid coordinates
  const handleResizeStop: RndResizeCallback = useCallback(
    (_e, _dir, ref, _delta, pos) => {
      setIsAtMinSize(false);
      const xMm = snapToGridMm(toMm(pos.x));
      const yMm = snapToGridMm(toMm(pos.y));
      const widthMm = snapToGridMm(toMm(parseInt(ref.style.width)));
      const heightMm = snapToGridMm(toMm(parseInt(ref.style.height)));
      updateComponent(config.id, {
        position: { x: xMm, y: yMm },
        size: { width: widthMm, height: heightMm },
      });
    },
    [config.id, toMm, snapToGridMm, updateComponent]
  );

  // Handle click to select
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedComponent(config.id);
    },
    [config.id, setSelectedComponent]
  );

  // Handle keyboard events for accessibility (Enter/Space to select)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedComponent(config.id);
      }
    },
    [config.id, setSelectedComponent]
  );

  // Memoize resize handle styles to avoid creating new objects on every render
  // Using 32px handles for better touch targets (minimum recommended 44px, but 32px balances usability and aesthetics)
  const resizeHandleStyles = useMemo<{ bottomRight: CSSProperties }>(
    () => ({
      bottomRight: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: isSelected ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
        bottom: '-16px',
        right: '-16px',
        border: '2px solid hsl(var(--background))',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        touchAction: 'none', // Prevent scroll interference on touch
      },
    }),
    [isSelected]
  );

  // Don't render if hidden (ghost mode handled separately)
  if (!config.visible) return null;

  // Component label for accessibility
  const componentLabel = t(COMPONENT_LABEL_KEYS[config.type]);

  // Note: We intentionally DON'T wrap Rnd in motion.div.
  // The motion.div wrapper was causing drag issues because:
  // 1. motion.div has no explicit position/size, so it doesn't fill the canvas
  // 2. Rnd's bounds="parent" calculates bounds based on motion.div, not the canvas
  // 3. This caused components to jump to y=0 when dragged
  // Instead, we rely on CSS transitions in the className for subtle animations.

  return (
    <Rnd
        position={position}
        size={size}
        bounds="parent"
        minWidth={minWidth}
        minHeight={minHeight}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        lockAspectRatio={constraints.aspectRatio}
        // NOTE: We do NOT use dragGrid/resizeGrid because they snap relative to
        // component starting position. Manual snapping in handlers ensures
        // absolute canvas grid alignment.
        onDragStop={handleDragStop}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disableDragging={config.locked}
        enableResizing={
          // Only show resize handle on selected, unlocked items
          !config.locked && isSelected
            ? { bottomRight: true }
            : false
        }
        tabIndex={0}
        role="button"
        aria-label={
          config.locked && isSelected
            ? t('production.draggable.componentAriaLabelLockedSelected', { name: componentLabel })
            : config.locked
              ? t('production.draggable.componentAriaLabelLocked', { name: componentLabel })
              : isSelected
                ? t('production.draggable.componentAriaLabelSelected', { name: componentLabel })
                : t('production.draggable.componentAriaLabel', { name: componentLabel })
        }
        aria-pressed={isSelected}
        className={cn(
          // Use specific transitions instead of transition-all to avoid
          // interfering with react-rnd's transform-based drag positioning
          'transition-colors transition-shadow duration-150 rounded-sm',
          // Selection state - using design tokens via Tailwind classes
          isSelected
            ? 'border-2 border-primary ring-2 ring-primary/20 shadow-sm'
            : 'border border-border hover:border-primary/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20',
          // Locked state - dashed border
          config.locked && 'border-dashed border-amber-400 cursor-not-allowed opacity-90',
          // Normal cursor
          !config.locked && 'cursor-move',
          // Constraint feedback - full opacity for better visibility
          isAtMinSize && 'ring-2 ring-amber-500',
          // Focus visible outline
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary'
        )}
        style={{ zIndex: isSelected ? 100 : (config.zIndex ?? 1) }}
        // Touch-friendly resize handles (24px for better touch targets) - memoized above
        resizeHandleStyles={resizeHandleStyles}
      >
        {/* Locked indicator badge */}
        {config.locked && (
          <div
            className="absolute -top-2 -right-2 bg-amber-500 rounded-full p-1 shadow-sm z-10"
            role="img"
            aria-label={t('production.draggable.lockedBadge')}
          >
            <Lock className="h-3 w-3 text-white" aria-hidden="true" />
          </div>
        )}

        {/* Min size feedback */}
        {isAtMinSize && (
          <div className="absolute -bottom-6 left-0 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded whitespace-nowrap z-10">
            {t('production.draggable.minSizeReached')}
          </div>
        )}

        {/* Component content */}
        <div className="w-full h-full overflow-hidden">
          {children}
        </div>
      </Rnd>
  );
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

/**
 * React.memo with custom comparison for performance.
 * Only re-renders when config props change.
 * Note: We intentionally exclude `children` from comparison because
 * React elements are recreated every render. ComponentRenderer is
 * memoized separately, so this is safe.
 */
export const DraggableComponent = React.memo(
  DraggableComponentInner,
  (prev, next) => {
    // Only re-render if config changed (children comparison excluded - always new refs)
    return (
      prev.config.id === next.config.id &&
      prev.config.type === next.config.type &&
      prev.config.position.x === next.config.position.x &&
      prev.config.position.y === next.config.position.y &&
      prev.config.size.width === next.config.size.width &&
      prev.config.size.height === next.config.size.height &&
      prev.config.visible === next.config.visible &&
      prev.config.locked === next.config.locked &&
      prev.config.zIndex === next.config.zIndex
    );
  }
);

DraggableComponent.displayName = 'DraggableComponent';
